/**
 * Norwegian Zod error map.
 *
 * Imported once at app startup ([main.tsx](../main.tsx)) to install the
 * default error map globally. Schemas can still pass an inline `error`
 * option to override the message for a specific check.
 *
 * Every visible string lives in `i18n/nb.ts` under the `validation`
 * namespace, so changing copy is a one-file change.
 */

import type { AnyFieldApi } from "@tanstack/react-form";
import { z } from "zod";

import { t } from "../i18n";

z.config({
  customError: (issue) => {
    switch (issue.code) {
      case "invalid_type":
        if (issue.input === undefined || issue.input === null) {
          return t.validation.required;
        }
        return t.validation.invalidType;

      case "too_small": {
        const min = Number(issue.minimum);
        if (issue.origin === "string") {
          if (issue.input === "" || issue.input === undefined) {
            return t.validation.required;
          }
          return t.validation.minLength(min);
        }
        if (issue.origin === "number" || issue.origin === "bigint") {
          return t.validation.minValue(min);
        }
        if (issue.origin === "array" || issue.origin === "set") {
          return min <= 1 ? t.validation.required : t.validation.minLength(min);
        }
        return undefined;
      }

      case "too_big": {
        const max = Number(issue.maximum);
        if (issue.origin === "string") return t.validation.maxLength(max);
        if (issue.origin === "number" || issue.origin === "bigint") {
          return t.validation.maxValue(max);
        }
        return undefined;
      }

      case "invalid_format": {
        if (issue.format === "email") return t.validation.email;
        return undefined;
      }

      case "invalid_value":
        return t.validation.selectOption;

      default:
        return undefined;
    }
  },
});

/**
 * Pull the first user-facing error off a `@tanstack/react-form` field.
 *
 * Only surfaces an error after the field has been touched (blurred at least
 * once or the form was submitted). Standard Schema issues come through as
 * `{ message: string }`; older string-shaped errors are also handled.
 */
export function fieldError(field: AnyFieldApi): string | undefined {
  if (!field.state.meta.isTouched) return undefined;
  const err = field.state.meta.errors[0];
  if (!err) return undefined;
  if (typeof err === "string") return err;
  if (typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return undefined;
}
